
import TradeModel from "@/models/Trade";
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import * as LightweightCharts from "lightweight-charts";
import TradingBotStats from "@/models/TradingBotStats";
import ExchangeMixin from "@/mixins/exchangeMixin";
import ThemeUpdatingView from "@/components/general/ThemeUpdatingView.vue";
import { PercentIndicator } from "@/mixins/percentIndicatorMixin";
import { formatDateFromAPI } from "@/utils/dateUtils";
import cloneDeep from "lodash/cloneDeep";

@Component({
  mixins: [ExchangeMixin, PercentIndicator],
})
export default class BotTradesChart extends ThemeUpdatingView {
  // 📦 The bot which is plotted
  @Prop({ default: null })
  public botStats!: TradingBotStats;

  // 📦 The bot's trade history that should be plotted
  @Prop({ default: [] })
  public tradeHistory!: TradeModel[];

  // 🚩 Flag for whether the chart is full screen
  @Prop({ default: false })
  public fullScreenMode!: boolean;

  // 📦 The currently selected filter for days
  @Prop({ default: 1 })
  public selectedFilterInDays!: number;

  // 📦 The total candlestick data
  private candleStickData!: any[];

  // 📦 The OHLC of a certain point
  private currentPointOHLC: any = null;

  // 📈 The current chart canvas
  private chart: any = null;

  // 📦 The candlestick series that should be plotted
  private series: any = null;

  // 📦 The volume series that should be plotted
  private volumeSeries: any = null;

  // 📦 The filtered candlestick data by time
  private getFilteredCandlestickData(): any[] {
    let data = cloneDeep(this.candleStickData);
    data = data.map((ohcl: any) => {
      ohcl.time = ohcl.time - new Date().getTimezoneOffset() * 60;
      return ohcl;
    });
    if (this.selectedFilterInDays == -1) {
      return data;
    } else {
      let latestDate = new Date().getTime() / 1000;
      let startTime = latestDate - this.selectedFilterInDays * 24 * 60 * 60;
      return data.filter((data: any) => data.time > startTime);
    }
  }

  // 📦 The width of the price scale fetched dynamically
  private get priceScaleWidth(): number {
    return this.chart.priceScale().width();
  }

  // 🗻 The current base chart configuration
  private get CHART_CONFIG(): any {
    return {
      timeScale: {
        timeVisible: true,
        borderColor: this.$vuetify.theme.currentTheme.chartGridColor as string,
        color: this.$vuetify.theme.currentTheme.chartLabel,
        tickMarkFormatter: (time: any) => formatDateFromAPI(time),
      },
      priceScale: {
        position: "left",
        borderColor: this.$vuetify.theme.currentTheme.chartLabel as string,
        color: this.$vuetify.theme.currentTheme.chartLabel,
      },
      watermark: {
        visible: true,
        fontSize: 14,
        horzAlign: "center",
        vertAlign: "center",
        fontFamily: "Aeroport",
        color: this.$vuetify.theme.currentTheme.chartGridColor,
        text: "One Click Crypto",
      },
      layout: {
        backgroundColor: this.$vuetify.theme.currentTheme.base as string,
        textColor: this.$vuetify.theme.currentTheme.chartLabel as string,
        fontFamily: "Aeroport",
      },
      crosshair: {
        mode: LightweightCharts.CrosshairMode.Normal,
        vertLine: {
          color: this.$vuetify.theme.currentTheme.primary as string,
        },
        horzLine: {
          color: this.$vuetify.theme.currentTheme.primary as string,
        },
      },
      grid: {
        horzLines: {
          color: this.$vuetify.theme.currentTheme.chartGridColor as string,
        },
        vertLines: {
          color: this.$vuetify.theme.currentTheme.chartGridColor as string,
        },
      },
    };
  }

  // 🗻 The current candlestick series configuration
  private get CHART_SERIES_CONFIG() {
    return {
      upColor: this.$vuetify.theme.currentTheme.chartBuyColor,
      downColor: this.$vuetify.theme.currentTheme.chartSellColor,
      wickUpColor: this.$vuetify.theme.currentTheme.chartBuyColor,
      wickDownColor: this.$vuetify.theme.currentTheme.chartSellColor,
      borderVisible: false,
      priceFormat: {
        precision:
          this.getFilteredCandlestickData()[0].open > 0 &&
          this.getFilteredCandlestickData()[0].open <= 1
            ? this.getFilteredCandlestickData()[0].open < 0.01
              ? 6
              : 4
            : 2,
        minMove:
          this.getFilteredCandlestickData()[0].open < 0.01 ? 0.000001 : 0.0001,
      },
    };
  }

  // 🗻 The current candlestick volume configuration
  private get CHART_VOLUME_CONFIG() {
    return {
      color: this.$vuetify.theme.currentTheme.primary,
      priceFormat: {
        type: "volume",
      },
      priceScaleId: "",
      scaleMargins: {
        top: 0.85,
        bottom: 0,
      },
    };
  }

  /**
   * 👂 Listener for changes in the selected day filter
   */
  @Watch("selectedFilterInDays")
  private onSelectedFilterInDaysChanged(val: number, oldVal: number) {
    this.createChart();
  }

  protected async mounted() {
    await this.fetchMarketData();
    this.createChart();

    // Listen to changes in the chart sizes
    window.addEventListener("resize", () => {
      this.chart.resize(
        (this.$refs.containerBotTradesChart as any).offsetWidth,
        (this.$refs.containerBotTradesChart as any).offsetHeight
      );
      this.chart.timeScale().fitContent();
    });
  }

  /**
   * 🎨 Callback for when light/dark mode updates
   */
  protected themeUpdate(): void {
    this.chart.applyOptions(this.CHART_CONFIG);
    this.series.applyOptions(this.CHART_SERIES_CONFIG);
    this.volumeSeries.applyOptions(this.CHART_VOLUME_CONFIG);
  }

  /**
   * 🚀 Fetches the OHLC data for the current bot
   */
  private async fetchMarketData() {
    const response = await Vue.prototype.$api.get("/api/v1/botmarketinfo", {
      botid: this.botStats.uid,
    });
    this.candleStickData = response.data.result;
  }

  /**
   * 🏗 Functionality to configure a TradingView chart
   */
  private createChart() {
    if (this.chart) {
      this.chart.remove();
    }

    this.chart = LightweightCharts.createChart(
      document.getElementById("container-bot-trades-chart") as any,
      this.CHART_CONFIG
    );

    this.series = this.chart.addCandlestickSeries(this.CHART_SERIES_CONFIG);
    this.volumeSeries = this.chart.addHistogramSeries(this.CHART_VOLUME_CONFIG);
    this.setDatasets();
    this.setChartMarkers();

    this.chart.subscribeCrosshairMove((param: any) => {
      if (param.time) {
        this.currentPointOHLC = param.seriesPrices.get(this.series);
        return;
      }
      this.currentPointOHLC = null;
    });

    this.chart.timeScale().fitContent();
  }

  /**
   * 🏗 Sets the series dataset to the chart
   */
  private setDatasets() {
    this.series.setData(this.getFilteredCandlestickData());
    this.volumeSeries.setData(
      this.getFilteredCandlestickData().map(
        (data: any) =>
          new Object({
            time: data.time,
            value: data.volume,
          }) as any
      )
    );
  }

  /**
   * 🏗 Sets the chart markers for part/full trade history
   */
  private setChartMarkers() {
    let markers = this.tradeHistory.map((trade: TradeModel) => {
      let formattedTime = new Date(
        (trade.date - new Date().getTimezoneOffset() * 60) * 1000
      );
      formattedTime.setMinutes(0, 0, 0);
      return new Object({
        time: formattedTime.getTime() / 1000,
        position: trade.type.toUpperCase() == "BUY" ? "belowBar" : "aboveBar",
        color: trade.type.toUpperCase() == "BUY" ? "#26a69a" : "#ef5350",
        shape: trade.type.toUpperCase() == "BUY" ? "arrowUp" : "arrowDown",
        text:
          trade.type.toUpperCase() +
          " (" +
          trade.signalStrength.toFixed(0) +
          "%)",
        // text:
        //   trade.type.toUpperCase() +
        //   " (" +
        //   trade.signalStrength.toFixed(0) +
        //   "%) " +
        //   trade.pRatio,
      }) as any;
    });

    if (this.selectedFilterInDays != -1) {
      let latestDate = new Date().getTime() / 1000;
      markers = markers.filter(
        (data: any) =>
          data.time > latestDate - this.selectedFilterInDays * 24 * 60 * 60
      );
    }

    this.series.setMarkers(markers);
  }
}
